import { useState, useEffect, useRef } from 'react';
import * as api from '../../api/analytics';

/**
 * ViewPickerDropdown — shows the current view name and lets users switch to any other view.
 *
 * Props:
 *   currentViewId  {string}  — ID of the currently open view
 *   currentViewName {string} — Display name for the current view
 *   onSelect       {fn}      — Called with (viewId) when the user picks a different view
 */
export default function ViewPickerDropdown({ currentViewId, currentViewName, onSelect }) {
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Load views when the dropdown is opened
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.fetchViews()
      .then(data => setViews(Array.isArray(data) ? data : (data?.views || [])))
      .catch(() => setViews([]))
      .finally(() => setLoading(false));
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleSelect(viewId) {
    setOpen(false);
    if (viewId !== currentViewId) {
      onSelect(viewId);
    }
  }

  return (
    <div className="relative" ref={dropdownRef} data-testid="view-picker">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 max-w-[200px]"
        data-testid="view-picker-btn"
        title={currentViewName || 'Switch view'}
      >
        <span className="truncate">{currentViewName || 'Switch view'}</span>
        <svg className="w-3 h-3 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded shadow-lg min-w-[220px] max-h-64 overflow-y-auto"
          data-testid="view-picker-dropdown"
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-400">Loading views...</div>
          ) : views.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No views found</div>
          ) : (
            views.map(v => (
              <button
                key={v.id}
                onClick={() => handleSelect(v.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                  v.id === currentViewId ? 'font-medium text-blue-700 bg-blue-50' : 'text-gray-700'
                }`}
                data-testid={`view-picker-item-${v.id}`}
              >
                {v.id === currentViewId && (
                  <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="truncate">{v.name || v.id}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
